// client/src/coms/views/watch_together_parts/watchTogetherUtils.js
import { voiceState } from '../../voip/voiceclient_parts/state';

export function getActiveVoiceRoom(preferredRoom) {
  if (preferredRoom && typeof preferredRoom === 'object') return preferredRoom;
  return voiceState && voiceState.activeRoom ? voiceState.activeRoom : null;
}

export function getServerKey(server) {
  const s = server && typeof server === 'object' ? server : {};
  return String(
    s.id != null
      ? s.id
      : s.public_id != null
        ? s.public_id
        : s.server_key != null
          ? s.server_key
          : s.server_id != null
            ? s.server_id
            : ''
  ).trim();
}

export function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function safeProvider(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'spotify') return 'spotify';
  if (raw === 'spotify') return 'spotify';
  if (raw === 'misc') return 'misc';
  if (raw === 'twitch') return 'twitch';
  return 'youtube';
}

export function getProviderLabel(value) {
  const provider = safeProvider(value);
  if (provider === 'spotify') return 'Spotify';
  if (provider === 'misc') return 'Misc';
  if (provider === 'twitch') return 'Twitch';
  return 'YouTube';
}

export function getProviderPlaceholder(value) {
  const provider = safeProvider(value);
  if (provider === 'spotify') {
    return 'Paste a Spotify track, album, playlist, artist, show, or episode link';
  }
  if (provider === 'misc') {
    return 'Paste a Vimeo, Dailymotion, Loom, Streamable, Wistia, MP4, WebM, MP3, or other video/audio link';
  }
  if (provider === 'twitch') {
    return 'Paste a Twitch channel or stream link';
  }
  return 'Paste a YouTube link or video ID';
}

export function computeRemainingSeconds(currentTime, duration) {
  const current = safeNumber(currentTime, 0);
  const total = safeNumber(duration, 0);
  if (total <= 0) return 0;
  return Math.max(0, total - current);
}

export function extractYouTubeVideoId(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';

  const directId = raw.match(/^[A-Za-z0-9_-]{11}$/);
  if (directId) return directId[0];

  try {
    const url = new URL(raw);
    const host = String(url.hostname || '').toLowerCase().replace(/^www\./, '');
    const path = String(url.pathname || '');

    if (host === 'youtu.be') {
      const part = path.split('/').filter(Boolean)[0] || '';
      return /^[A-Za-z0-9_-]{11}$/.test(part) ? part : '';
    }

    if (
      host === 'youtube.com' ||
      host === 'm.youtube.com' ||
      host === 'music.youtube.com' ||
      host === 'youtube-nocookie.com'
    ) {
      const fromQuery = String(url.searchParams.get('v') || '').trim();
      if (/^[A-Za-z0-9_-]{11}$/.test(fromQuery)) return fromQuery;

      const parts = path.split('/').filter(Boolean);
      if (parts.length >= 2 && (parts[0] === 'embed' || parts[0] === 'shorts' || parts[0] === 'live')) {
        return /^[A-Za-z0-9_-]{11}$/.test(parts[1]) ? parts[1] : '';
      }
    }
  } catch {}

  const loose = raw.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{11})/i);
  return loose ? loose[1] : '';
}

function extractSpotifyInfo(input) {
  const raw = String(input || '').trim();
  if (!raw) {
    return {
      mediaType: '',
      mediaId: '',
      mediaUri: '',
      shareUrl: '',
    };
  }

  const uriMatch = raw.match(/^spotify:(track|album|playlist|artist|show|episode):([A-Za-z0-9]{22})$/i);
  if (uriMatch) {
    const mediaType = String(uriMatch[1] || '').toLowerCase();
    const mediaId = String(uriMatch[2] || '').trim();
    return {
      mediaType,
      mediaId,
      mediaUri: `spotify:${mediaType}:${mediaId}`,
      shareUrl: `https://open.spotify.com/${mediaType}/${mediaId}`,
    };
  }

  const directId = raw.match(/^[A-Za-z0-9]{22}$/);
  if (directId) {
    const mediaId = directId[0];
    return {
      mediaType: 'track',
      mediaId,
      mediaUri: `spotify:track:${mediaId}`,
      shareUrl: `https://open.spotify.com/track/${mediaId}`,
    };
  }

  try {
    const url = new URL(raw);
    const host = String(url.hostname || '').toLowerCase().replace(/^www\./, '');
    const path = String(url.pathname || '');
    const parts = path.split('/').filter(Boolean);

    if ((host === 'open.spotify.com' || host === 'play.spotify.com') && parts.length >= 2) {
      const mediaType = String(parts[0] || '').toLowerCase();
      const mediaId = String(parts[1] || '').trim();

      if (/^(track|album|playlist|artist|show|episode)$/.test(mediaType) && /^[A-Za-z0-9]{22}$/.test(mediaId)) {
        return {
          mediaType,
          mediaId,
          mediaUri: `spotify:${mediaType}:${mediaId}`,
          shareUrl: `https://open.spotify.com/${mediaType}/${mediaId}`,
        };
      }
    }

    if (host === 'spotify.link') {
      const uri = String(url.searchParams.get('uri') || '').trim();
      const nested = extractSpotifyInfo(uri);
      if (nested.mediaType && nested.mediaId) return nested;
    }
  } catch {}

  const loose = raw.match(/spotify\.com\/(track|album|playlist|artist|show|episode)\/([A-Za-z0-9]{22})/i);
  if (loose) {
    const mediaType = String(loose[1] || '').toLowerCase();
    const mediaId = String(loose[2] || '').trim();
    return {
      mediaType,
      mediaId,
      mediaUri: `spotify:${mediaType}:${mediaId}`,
      shareUrl: `https://open.spotify.com/${mediaType}/${mediaId}`,
    };
  }

  return {
    mediaType: '',
    mediaId: '',
    mediaUri: '',
    shareUrl: '',
  };
}


function miscThumbnailFallback(label = 'Media') {
  const text = encodeURIComponent(String(label || 'Media'));
  return `data:image/svg+xml;utf8,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 450"%3E%3Cdefs%3E%3ClinearGradient id="g" x1="0" x2="1" y1="0" y2="1"%3E%3Cstop offset="0" stop-color="%23111827"/%3E%3Cstop offset="1" stop-color="%23334155"/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width="800" height="450" fill="url(%23g)"/%3E%3Ccircle cx="400" cy="225" r="82" fill="rgba(255,255,255,0.12)"/%3E%3Cpolygon points="375,180 375,270 450,225" fill="white"/%3E%3Ctext x="400" y="360" text-anchor="middle" fill="%23ffffff" font-family="Arial, sans-serif" font-size="30" font-weight="700"%3E${text}%3C/text%3E%3C/svg%3E`;
}

function isLikelyUrl(value) {
  try {
    const url = new URL(String(value || '').trim());
    return /^https?:$/i.test(url.protocol);
  } catch {
    return false;
  }
}

function normalizeMiscUrl(input) {
  const raw = String(input || '').trim();
  if (!raw || !isLikelyUrl(raw)) return null;

  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  const host = String(url.hostname || '').toLowerCase().replace(/^www\./, '');
  const path = String(url.pathname || '');
  const lastPart = path.split('/').filter(Boolean).pop() || '';
  const cleanLastPart = lastPart.split('?')[0].split('#')[0];
  const extMatch = cleanLastPart.match(/\.([a-z0-9]{2,5})$/i);
  const ext = extMatch ? String(extMatch[1] || '').toLowerCase() : '';
  const videoExts = new Set(['mp4', 'webm', 'ogg', 'ogv', 'm3u8']);
  const audioExts = new Set(['mp3', 'wav', 'm4a', 'aac', 'flac', 'oga', 'opus']);

  const build = (overrides = {}) => {
    const mediaId = String(overrides.mediaId || raw).trim();
    const mediaType = String(overrides.mediaType || 'embed').trim();
    const mediaKind = String(overrides.mediaKind || 'iframe').trim();
    const hostLabel = String(overrides.hostLabel || host || 'misc').trim();
    const shareUrl = String(overrides.shareUrl || raw).trim();
    const sourceUrl = String(overrides.sourceUrl || raw).trim();
    const embedUrl = String(overrides.embedUrl || (mediaKind === 'iframe' ? raw : sourceUrl)).trim();
    return {
      ok: true,
      provider: 'misc',
      value: raw,
      mediaId,
      mediaType,
      mediaUri: sourceUrl,
      shareUrl,
      sourceUrl,
      embedUrl,
      mediaKind,
      hostLabel,
      thumbnailUrl: String(overrides.thumbnailUrl || miscThumbnailFallback(hostLabel)).trim(),
      title: String(overrides.title || '').trim(),
    };
  };

  if (videoExts.has(ext)) return build({ mediaType: 'video', mediaKind: 'direct', hostLabel: 'Direct Video' });
  if (audioExts.has(ext)) return build({ mediaType: 'audio', mediaKind: 'direct', hostLabel: 'Direct Audio' });

  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const parts = path.split('/').filter(Boolean);
    const id = parts.filter((part) => /^\d+$/.test(part)).pop() || '';
    if (id) return build({ mediaId: `vimeo:${id}`, embedUrl: `https://player.vimeo.com/video/${id}`, hostLabel: 'Vimeo' });
  }

  if (host === 'dailymotion.com' || host === 'dai.ly') {
    const idMatch = raw.match(/(?:video\/|dai\.ly\/)([A-Za-z0-9]+)/i);
    const id = idMatch ? idMatch[1] : '';
    if (id) return build({ mediaId: `dailymotion:${id}`, embedUrl: `https://www.dailymotion.com/embed/video/${id}`, hostLabel: 'Dailymotion' });
  }

  if (host === 'loom.com') {
    const idMatch = path.match(/\/(?:share|embed)\/([A-Za-z0-9]+)/i);
    const id = idMatch ? idMatch[1] : '';
    if (id) return build({ mediaId: `loom:${id}`, embedUrl: `https://www.loom.com/embed/${id}`, hostLabel: 'Loom' });
  }

  if (host === 'streamable.com') {
    const id = path.split('/').filter(Boolean).pop() || '';
    if (id) return build({ mediaId: `streamable:${id}`, embedUrl: `https://streamable.com/e/${id}`, hostLabel: 'Streamable' });
  }

  if (host.includes('wistia.com') || host.includes('wi.st')) {
    const idMatch = raw.match(/(?:medias|iframe)\/([A-Za-z0-9]+)/i);
    const id = idMatch ? idMatch[1] : '';
    if (id) return build({ mediaId: `wistia:${id}`, embedUrl: `https://fast.wistia.net/embed/iframe/${id}`, hostLabel: 'Wistia' });
  }

  return build({ mediaId: raw, mediaType: 'embed', mediaKind: 'iframe', hostLabel: host || 'Misc Host', embedUrl: raw, sourceUrl: raw });
}

export function normalizeMiscInput(input) {
  const raw = String(input || '').trim();
  const info = normalizeMiscUrl(raw);
  if (info) return info;
  return {
    ok: false,
    provider: 'misc',
    value: raw,
    mediaId: '',
    mediaType: '',
    mediaUri: '',
    shareUrl: '',
    sourceUrl: '',
    embedUrl: '',
    mediaKind: '',
    hostLabel: '',
    thumbnailUrl: '',
    title: '',
  };
}
export function extractSpotifyTrackId(input) {
  const info = extractSpotifyInfo(input);
  return info.mediaType === 'track' ? info.mediaId : '';
}

export function normalizeYouTubeInput(input) {
  const raw = String(input || '').trim();
  const videoId = extractYouTubeVideoId(raw);
  if (!videoId) {
    return {
      ok: false,
      provider: 'youtube',
      value: raw,
      videoId: '',
      mediaId: '',
      mediaType: 'video',
      mediaUri: '',
      shareUrl: '',
      thumbnailUrl: '',
      title: '',
    };
  }

  return {
    ok: true,
    provider: 'youtube',
    value: raw,
    videoId,
    mediaId: videoId,
    mediaType: 'video',
    mediaUri: '',
    shareUrl: `https://www.youtube.com/watch?v=${videoId}`,
    thumbnailUrl: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
    title: '',
  };
}

export function normalizeSpotifyInput(input) {
  const raw = String(input || '').trim();
  const info = extractSpotifyInfo(raw);
  if (!info.mediaId) {
    return {
      ok: false,
      provider: 'spotify',
      value: raw,
      trackId: '',
      mediaId: '',
      mediaType: '',
      mediaUri: '',
      shareUrl: '',
      thumbnailUrl: '',
      title: '',
    };
  }

  return {
    ok: true,
    provider: 'spotify',
    value: raw,
    trackId: info.mediaType === 'track' ? info.mediaId : '',
    mediaId: info.mediaId,
    mediaType: info.mediaType,
    mediaUri: info.mediaUri,
    shareUrl: info.shareUrl,
    thumbnailUrl: '',
    title: '',
  };
}

export function normalizeDraftInput(input, provider = 'youtube') {
  const normalizedProvider = safeProvider(provider);
  if (normalizedProvider === 'spotify') {
    return normalizeSpotifyInput(input);
  }
  if (normalizedProvider === 'misc') {
    return normalizeMiscInput(input);
  }
  return normalizeYouTubeInput(input);
}

function spotifyThumbnailFallback() {
  return 'data:image/svg+xml;utf8,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 450"%3E%3Crect width="800" height="450" fill="%23121212"/%3E%3Ccircle cx="400" cy="225" r="100" fill="%231db954"/%3E%3Cpath d="M353 188c53-15 111-9 166 17" stroke="%23121212" stroke-width="16" stroke-linecap="round" fill="none"/%3E%3Cpath d="M362 223c42-12 87-7 131 13" stroke="%23121212" stroke-width="14" stroke-linecap="round" fill="none"/%3E%3Cpath d="M372 256c31-8 61-5 92 9" stroke="%23121212" stroke-width="12" stroke-linecap="round" fill="none"/%3E%3Ctext x="400" y="355" text-anchor="middle" fill="%23ffffff" font-family="Arial, sans-serif" font-size="34" font-weight="700"%3ESpotify%3C/text%3E%3C/svg%3E';
}

export function queueEntryFromDraft(draft, userName, identity) {
  if (!draft || !draft.ok) return null;

  const provider = safeProvider(draft.provider);

  if (provider === 'misc') {
    const mediaId = String(draft.mediaId || draft.mediaUri || draft.sourceUrl || '').trim();
    const mediaType = String(draft.mediaType || 'embed').trim().toLowerCase() || 'embed';
    const mediaUri = String(draft.mediaUri || draft.sourceUrl || draft.embedUrl || '').trim();
    const embedUrl = String(draft.embedUrl || draft.sourceUrl || '').trim();
    const sourceUrl = String(draft.sourceUrl || draft.mediaUri || draft.embedUrl || '').trim();
    const mediaKind = String(draft.mediaKind || (mediaType === 'audio' || mediaType === 'video' ? 'direct' : 'iframe')).trim();
    if (!mediaId || !(embedUrl || sourceUrl)) return null;

    return {
      id: `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`,
      provider: 'misc',
      mediaId,
      mediaType,
      mediaUri,
      shareUrl: String(draft.shareUrl || sourceUrl || embedUrl).trim(),
      sourceUrl,
      embedUrl,
      mediaKind,
      hostLabel: String(draft.hostLabel || 'Misc').trim(),
      thumbnailUrl: String(draft.thumbnailUrl || '').trim() || miscThumbnailFallback(draft.hostLabel || 'Media'),
      title: String(draft.title || '').trim(),
      addedAt: Date.now(),
      addedBy: String(userName || '').trim(),
      addedByIdentity: String(identity || '').trim(),
    };
  }

  if (provider === 'spotify') {
    const mediaType = String(draft.mediaType || '').trim().toLowerCase();
    const mediaId = String(draft.mediaId || draft.trackId || '').trim();
    const mediaUri = String(draft.mediaUri || (mediaType && mediaId ? `spotify:${mediaType}:${mediaId}` : '')).trim();

    if (!mediaType || !mediaId || !mediaUri) return null;

    return {
      id: `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`,
      provider: 'spotify',
      mediaId,
      mediaType,
      mediaUri,
      trackId: mediaType === 'track' ? mediaId : '',
      shareUrl: String(draft.shareUrl || `https://open.spotify.com/${mediaType}/${mediaId}`).trim(),
      thumbnailUrl: String(draft.thumbnailUrl || '').trim() || spotifyThumbnailFallback(),
      title: String(draft.title || '').trim(),
      addedAt: Date.now(),
      addedBy: String(userName || '').trim(),
      addedByIdentity: String(identity || '').trim(),
    };
  }

  const videoId = String(draft.mediaId || draft.videoId || '').trim();
  if (!videoId) return null;

  return {
    id: `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`,
    provider: 'youtube',
    mediaId: videoId,
    mediaType: 'video',
    mediaUri: '',
    videoId,
    shareUrl: String(draft.shareUrl || `https://www.youtube.com/watch?v=${videoId}`).trim(),
    thumbnailUrl: String(draft.thumbnailUrl || `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`).trim(),
    title: String(draft.title || '').trim(),
    addedAt: Date.now(),
    addedBy: String(userName || '').trim(),
    addedByIdentity: String(identity || '').trim(),
  };
}

export function itemMediaKey(item) {
  const provider = safeProvider(item && item.provider);

  if (provider === 'spotify') {
    const mediaType = String((item && item.mediaType) || '').trim().toLowerCase();
    const mediaId = String((item && item.mediaId) || (item && item.trackId) || '').trim();
    if (mediaType && mediaId) return `spotify:${mediaType}:${mediaId}`;
    return mediaId ? `spotify:${mediaId}` : '';
  }

  if (provider === 'youtube') {
    const videoId = String((item && item.mediaId) || (item && item.videoId) || '').trim();
    return videoId ? `youtube:${videoId}` : '';
  }

  if (provider === 'misc') {
    const mediaId = String((item && item.mediaId) || (item && item.mediaUri) || '').trim();
    return mediaId ? `misc:${mediaId}` : '';
  }

  const fallbackId = String((item && item.id) || '').trim();
  return fallbackId ? `${provider}:${fallbackId}` : '';
}

export function playbackMediaKey(playback) {
  const provider = safeProvider(playback && playback.provider);

  if (provider === 'spotify') {
    const mediaType = String((playback && playback.mediaType) || '').trim().toLowerCase();
    const mediaId = String((playback && playback.mediaId) || (playback && playback.trackId) || '').trim();
    if (mediaType && mediaId) return `spotify:${mediaType}:${mediaId}`;
    return mediaId ? `spotify:${mediaId}` : '';
  }

  if (provider === 'youtube') {
    const videoId = String((playback && playback.mediaId) || (playback && playback.videoId) || '').trim();
    return videoId ? `youtube:${videoId}` : '';
  }

  if (provider === 'misc') {
    const mediaId = String((playback && playback.mediaId) || (playback && playback.mediaUri) || '').trim();
    return mediaId ? `misc:${mediaId}` : '';
  }

  return '';
}

export function buildYouTubeEmbedUrl(videoId, autoplay = false, startSeconds = 0) {
  const id = String(videoId || '').trim();
  if (!id) return '';
  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
    enablejsapi: '1',
    controls: '1',
    origin: window.location.origin,
  });
  if (startSeconds > 0) {
    params.set('start', String(Math.floor(startSeconds)));
  }
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}

export function buildSpotifyEmbedUrl(trackOrItem, autoplay = false) {
  let mediaType = 'track';
  let mediaId = '';

  if (trackOrItem && typeof trackOrItem === 'object') {
    mediaType = String(trackOrItem.mediaType || 'track').trim().toLowerCase() || 'track';
    mediaId = String(trackOrItem.mediaId || trackOrItem.trackId || '').trim();
  } else {
    mediaId = String(trackOrItem || '').trim();
  }

  if (!mediaId) return '';

  const params = new URLSearchParams({
    theme: '0',
  });

  if (autoplay) {
    params.set('utm_source', 'generator');
  }

  return `https://open.spotify.com/embed/${mediaType}/${mediaId}?${params.toString()}`;
}

export function buildEmbedUrlForItem(item, opts = {}, legacyStartSeconds = 0) {
  const provider = safeProvider(item && item.provider);
  const autoplay = typeof opts === 'boolean' ? !!opts : !!(opts && opts.autoplay);
  const startSeconds = typeof opts === 'boolean' ? safeNumber(legacyStartSeconds, 0) : safeNumber(opts && opts.startSeconds, 0);

  if (provider === 'spotify') {
    return buildSpotifyEmbedUrl(item, autoplay);
  }

  if (provider === 'youtube') {
    return buildYouTubeEmbedUrl(
      String((item && item.mediaId) || (item && item.videoId) || '').trim(),
      autoplay,
      startSeconds
    );
  }

  if (provider === 'misc') {
    const mediaKind = String((item && item.mediaKind) || '').trim().toLowerCase();
    const sourceUrl = String((item && item.sourceUrl) || (item && item.mediaUri) || '').trim();
    const embedUrl = String((item && item.embedUrl) || sourceUrl).trim();
    if (mediaKind === 'direct') return sourceUrl || embedUrl;
    return embedUrl || sourceUrl;
  }

  return '';
}

export function normalisePlayback(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const provider = safeProvider(raw.provider);

  if (provider === 'spotify') {
    const mediaType = String(raw.mediaType || '').trim().toLowerCase();
    const mediaId = String(raw.mediaId || raw.trackId || '').trim();
    if (!mediaId) return null;

    const currentTime = Number(raw.currentTime);

    return {
      provider: 'spotify',
      mediaId,
      mediaType: mediaType || 'track',
      mediaUri: String(raw.mediaUri || (mediaType && mediaId ? `spotify:${mediaType}:${mediaId}` : '')).trim(),
      trackId: mediaType === 'track' ? mediaId : '',
      currentTime: Number.isFinite(currentTime) && currentTime >= 0 ? currentTime : 0,
      playing: !!raw.playing,
      updatedAt: Number(raw.updatedAt) || Date.now(),
    };
  }

  if (provider === 'misc') {
    const mediaId = String(raw.mediaId || raw.sourceUrl || raw.mediaUri || '').trim();
    if (!mediaId) return null;

    const currentTime = Number(raw.currentTime);

    return {
      provider: 'misc',
      mediaId,
      mediaType: String(raw.mediaType || 'embed').trim().toLowerCase() || 'embed',
      mediaUri: String(raw.mediaUri || raw.sourceUrl || raw.embedUrl || '').trim(),
      currentTime: Number.isFinite(currentTime) && currentTime >= 0 ? currentTime : 0,
      playing: !!raw.playing,
      updatedAt: Number(raw.updatedAt) || Date.now(),
    };
  }

  const videoId = String(raw.mediaId || raw.videoId || '').trim();
  if (!videoId) return null;

  const currentTime = Number(raw.currentTime);

  return {
    provider: 'youtube',
    mediaId: videoId,
    mediaType: 'video',
    mediaUri: '',
    videoId,
    currentTime: Number.isFinite(currentTime) && currentTime >= 0 ? currentTime : 0,
    playing: !!raw.playing,
    updatedAt: Number(raw.updatedAt) || Date.now(),
  };
}

export function normalizeSkipVote(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      queueId: '',
      identities: [],
      updatedAt: 0,
    };
  }

  const queueId = String(raw.queueId || '').trim();
  const identities = Array.isArray(raw.identities)
    ? Array.from(new Set(raw.identities.map((value) => String(value || '').trim()).filter(Boolean)))
    : [];

  return {
    queueId,
    identities,
    updatedAt: Number(raw.updatedAt) || 0,
  };
}

export function computePlaybackTime(playback) {
  const p = normalisePlayback(playback);
  if (!p) return 0;
  if (!p.playing) return Math.max(0, p.currentTime);
  const elapsed = Math.max(0, (Date.now() - p.updatedAt) / 1000);
  return Math.max(0, p.currentTime + elapsed);
}